import { useState } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';
import Avatar from '@/Components/Avatar';
import ConfirmModal from '@/Components/ConfirmModal';

export default function UsersIndex({ users, roles, filters }) {
    const { auth } = usePage().props;
    const [search, setSearch] = useState(filters.search || '');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const handleSearch = (e) => {
        e.preventDefault();
        router.get('/users', { search, role: filters.role, status: filters.status }, {
            preserveState: true,
            replace: true,
        });
    };

    const handleFilter = (key, value) => {
        router.get('/users', { ...filters, [key]: value }, {
            preserveState: true,
            replace: true,
        });
    };

    const handleToggleStatus = (user) => {
        router.patch(`/users/${user.id}/toggle-status`, {}, {
            preserveScroll: true,
        });
    };

    const handleDelete = () => {
        router.delete(`/users/${userToDelete.id}`, {
            onSuccess: () => {
                setShowDeleteModal(false);
                setUserToDelete(null);
            },
        });
    };

    const canManageUser = (user) => {
        if (auth.user.roles.includes('Super Admin')) return true;
        if (user.roles.some(r => r.name === 'Super Admin')) return false;
        return true;
    };

    return (
        <AppLayout title="Users">
            <PageHeader
                title="Users"
                subtitle="Manage user accounts and permissions"
                breadcrumbs={[
                    { label: 'Settings' },
                    { label: 'Users' },
                ]}
                actions={
                    <Link href="/users/create" className="btn btn-primary">
                        <i className="bi bi-plus-lg"></i>
                        <span>Add User</span>
                    </Link>
                }
            />

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-4">
                            <form onSubmit={handleSearch}>
                                <div className="input-group">
                                    <span className="input-group-text bg-white">
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search users..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </form>
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={filters.role || ''}
                                onChange={(e) => handleFilter('role', e.target.value)}
                            >
                                <option value="">All Roles</option>
                                {roles.map((role) => (
                                    <option key={role} value={role}>{role}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={filters.status || ''}
                                onChange={(e) => handleFilter('status', e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <Link
                                href="/users"
                                className="btn btn-outline-secondary w-100"
                            >
                                <i className="bi bi-x-lg me-1"></i>
                                Clear
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Users Table */}
            <div className="card">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Last Login</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.data.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-5">
                                        <div className="text-muted">
                                            <i className="bi bi-people fs-1 d-block mb-3"></i>
                                            No users found
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                users.data.map((user) => (
                                    <tr key={user.id}>
                                        <td>
                                            <div className="d-flex align-items-center gap-3">
                                                <Avatar name={user.name} size="md" />
                                                <div>
                                                    <div className="fw-semibold">{user.name}</div>
                                                    <div className="text-muted small">{user.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            {user.roles.map((role) => (
                                                <span
                                                    key={role.name}
                                                    className={`badge me-1 ${
                                                        role.name === 'Super Admin' ? 'bg-danger' :
                                                        role.name === 'Admin' ? 'bg-warning text-dark' :
                                                        'badge-primary'
                                                    }`}
                                                >
                                                    {role.name}
                                                </span>
                                            ))}
                                        </td>
                                        <td>
                                            <span className={`badge ${user.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                                {user.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="text-muted">
                                            {user.last_login_at || 'Never'}
                                        </td>
                                        <td className="text-end">
                                            {canManageUser(user) && (
                                                <div className="btn-group">
                                                    <Link
                                                        href={`/users/${user.id}/edit`}
                                                        className="btn btn-sm btn-outline-secondary"
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </Link>
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => handleToggleStatus(user)}
                                                        title={user.is_active ? 'Deactivate' : 'Activate'}
                                                    >
                                                        <i className={`bi ${user.is_active ? 'bi-toggle-on text-success' : 'bi-toggle-off'}`}></i>
                                                    </button>
                                                    {user.id !== auth.user.id && (
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => {
                                                                setUserToDelete(user);
                                                                setShowDeleteModal(true);
                                                            }}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {users.last_page > 1 && (
                    <div className="card-footer d-flex justify-content-between align-items-center">
                        <div className="text-muted small">
                            Showing {users.from} to {users.to} of {users.total} users
                        </div>
                        <nav>
                            <ul className="pagination pagination-sm mb-0">
                                {users.links.map((link, index) => (
                                    <li
                                        key={index}
                                        className={`page-item ${link.active ? 'active' : ''} ${!link.url ? 'disabled' : ''}`}
                                    >
                                        <Link
                                            href={link.url || '#'}
                                            className="page-link"
                                            dangerouslySetInnerHTML={{ __html: link.label }}
                                            preserveScroll
                                        />
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                )}
            </div>

            <ConfirmModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete User"
                message={`Are you sure you want to delete ${userToDelete?.name}? This action cannot be undone.`}
                confirmText="Delete User"
                variant="danger"
            />
        </AppLayout>
    );
}
